import { Link } from 'react-router-dom';
import FileUploadCard from './form/FileUploadCard';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '@/lib/i18n/useLocale';
import { useId, useMemo, useState } from 'react';
import { isCrmPortalProductQuery } from '../config/productFromUrl';
import { SignUpFormProps } from '../types/configs.types';

const SignUpForm = ({ form, productQuery, onSubmit, loading, error, success, presetProjectType }: SignUpFormProps) => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    const termsId = useId();
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const productKey = productQuery ?? '';

    const isCrmPortalSignup = useMemo(() => isCrmPortalProductQuery(productKey), [productKey]);

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 w-full space-y-3 lg:mt-9">
                {/* <div className="flex sm:flex-row flex-col gap-4 w-full justify-between"> */}
                {/* name */}
                <div className="w-full">
                    <label className="block mb-1 text-[16px] font-medium text-primery-blue-900">
                        {t('signUp.name')}
                    </label>
                    <Input
                        type="text"
                        {...form.register('name', { required: t('signUp.nameRequired') })}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primery-blue-800"
                        placeholder={t('signUp.namePlaceholder')}
                    />
                    {form.formState.errors.name && (
                        <p className="text-red-500 text-sm mt-1">{form.formState.errors.name.message}</p>
                    )}
                </div>

                {/* email */}
                <div className="w-full">
                    <label className="block mb-1 text-[16px] font-medium text-primery-blue-900">
                        {t('signUp.email')}
                    </label>
                    <Input
                        type="email"
                        {...form.register('email', {
                            required: t('signUp.emailRequired'),
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('signUp.emailInvalid'),
                            },
                        })}
                        className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primery-blue-800"
                        placeholder={t('signUp.emailPlaceholder')}
                    />
                    {form.formState.errors.email && (
                        <p className="text-red-500 text-sm mt-1">{form.formState.errors.email.message}</p>
                    )}
                </div>
                {/* </div> */}

                <div className="flex sm:flex-row flex-col gap-4 w-full justify-between">
                    {/* phone */}
                    <div className="w-full">
                        <label className="block mb-1 text-[16px] font-medium text-primery-blue-900">
                            {t('signUp.phone')}
                        </label>
                        <Input
                            type="text"
                            {...form.register('phone', { required: t('signUp.phoneRequired') })}
                            className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primery-blue-800"
                            placeholder={t('signUp.phonePlaceholder')}
                        />
                        {form.formState.errors.phone && (
                            <p className="text-red-500 text-sm mt-1">{form.formState.errors.phone.message}</p>
                        )}
                    </div>
                    {/* password */}
                    <div className="w-full">
                        <label className="block mb-1 text-[16px] font-medium text-primery-blue-900">
                            {t('signUp.password')}
                        </label>
                        <Input
                            type="password"
                            {...form.register('password', {
                                required: t('signUp.passwordRequired'),
                                minLength: {
                                    value: 6,
                                    message: t('signUp.passwordMinLength'),
                                },
                            })}
                            className=" p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primery-blue-800 w-full"
                            placeholder={t('signUp.passwordPlaceholder')}
                        />
                        {form.formState.errors.password && (
                            <p className="text-red-500 text-sm mt-1">{form.formState.errors.password.message}</p>
                        )}
                    </div>
                </div>

                {/* project_type: CRM portal shows select; other presets use hidden; else full select */}
                <div className="w-full">
                    {(!presetProjectType || isCrmPortalSignup) && (
                        <label className="block mb-1 text-[16px] font-medium text-primery-blue-900">
                            {isCrmPortalSignup ? t('loginPage.crmPortalSelectLabel') : t('signUp.projectType')}
                        </label>
                    )}
                    <Controller
                        name="project_type"
                        // className="bg-transparent"
                        control={form.control}
                        rules={{
                            required: presetProjectType && !isCrmPortalSignup ? false : t('signUp.projectTypeRequired'),
                        }}
                        render={({ field }) =>
                            isCrmPortalSignup ? (
                                <select
                                    {...field}
                                    className="w-full p-3 rounded-lg border bg-transparent border-gray-300 focus:outline-none focus:ring-2 focus:ring-primery-blue-800"
                                >
                                    <option value="crm">{t('signUp.productsCrm')}</option>
                                    <option value="gyms">{t('signUp.gyms')}</option>
                                    <option value="educational_centers">{t('signUp.educational')}</option>
                                    <option value="clinics">{t('signUp.clinics')}</option>
                                </select>
                            ) : presetProjectType ? (
                                <input type="hidden" {...field} />
                            ) : (
                                <select
                                    {...field}
                                    className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 bg-transparent"
                                >
                                    <option className="dark:bg-[#191919]" value="">
                                        {t('signUp.projectTypePlaceholder')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="crm">
                                        {t('signUp.productsCrm')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="fleet">
                                        {t('signUp.fleet')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="hr_management">
                                        {t('signUp.hrManagement')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="clinico">
                                        {t('signUp.productsClinico')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="gyms">
                                        {t('signUp.gyms')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="educational_centers">
                                        {t('signUp.educational')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="hotels">
                                        {t('signUp.hotels')}
                                    </option>
                                    <option className="dark:bg-[#191919]" value="real_estate">
                                        {t('signUp.realEstate')}
                                    </option>
                                </select>
                            )
                        }
                    />
                    {(!presetProjectType || isCrmPortalSignup) && form.formState.errors.project_type ? (
                        <p className="text-red-500 text-sm mt-1">{form.formState.errors.project_type.message}</p>
                    ) : null}
                </div>

                <FileUploadCard name="company_logo" label={t('signUp.companyLogo')} />

                {error && <p className="text-red-500 text-[14px] text-center">{error}</p>}
                {success && <p className="text-green-500 text-[14px] text-center">{success}</p>}
                <label htmlFor={termsId} className="flex items-start justify-center gap-2 py-4 text-center">
                    <input
                        id={termsId}
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(event) => setAcceptedTerms(event.target.checked)}
                        required
                        className="mt-1 h-4 w-4 shrink-0 accent-[#145DA0]"
                    />
                    <span className="text-primery-blue-700 text-[14px]">
                        {t('signUp.termsAgree')}
                        <Link className="text-primery-blue-500 text-[14px]" to={localizedPath('/terms-and-conditions')}>
                            {' '}
                            {t('signUp.termsLink')} {''}
                        </Link>
                        {t('signUp.termsSuffix')}
                    </span>
                </label>
                <div>
                    <button
                        type="submit"
                        disabled={loading || !acceptedTerms}
                        className="w-full py-3 bg-primery-blue-700 text-white-blue-50 rounded-lg font-cairo font-bold hover:bg-primery-blue-600 transition disabled:opacity-50"
                    >
                        {loading ? t('signUp.submitting') : t('signUp.submit')}
                    </button>
                </div>
                <div className="pt-2 text-center">
                    <p className="text-[14px] text-primery-blue-900">
                        {t('signUp.alreadyHave')}
                        <Link className="text-[14px] text-primery-blue-500" to={localizedPath('/login')}>
                            {' '}
                            {t('signUp.loginLink')}{' '}
                        </Link>
                    </p>
                </div>
            </form>
        </Form>
    );
};

export default SignUpForm;
